import { LoadingButton } from '@mui/lab';
import { Button, Stack, useTheme } from '@mui/material';
import { OnboardingTestIds } from './OnboardingPage';
import { useOnboardingStore } from '@diana/core';
import { ONBOARDING_PAGE_NS } from './i18n';
import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';

export interface IOnboardingButtons {
  stepsLength: number;
  onCancel?: () => void;
  onSubmit?: () => void;
  isLoading?: boolean;
  disableSubmitButton?: boolean;
}

export const OnboardingButtons = ({
  stepsLength,
  onCancel,
  onSubmit,
  isLoading = false,
  disableSubmitButton = false,
}: IOnboardingButtons) => {
  const { palette } = useTheme();
  const { t } = useTranslation(ONBOARDING_PAGE_NS);

  const currentStepIndex = useOnboardingStore((store) => store.stepIndex);
  const setCurrentStepIndex = useOnboardingStore((store) => store.setStepIndex);

  const handleBackClick = useCallback(() => {
    onCancel?.();
    setCurrentStepIndex(currentStepIndex - 1);
  }, [setCurrentStepIndex, currentStepIndex, onCancel]);

  const handleNextClick = useCallback(() => {
    onSubmit?.();
    if (currentStepIndex !== stepsLength - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    }
  }, [setCurrentStepIndex, stepsLength, currentStepIndex, onSubmit]);

  return (
    <Stack flexDirection="row" justifyContent="space-between" mt={1} gap={2}>
      <Button
        data-testid={OnboardingTestIds.onCancel}
        onClick={handleBackClick}
        disabled={currentStepIndex === 0}
        sx={{
          color: palette.text.primary,
          '&:hover': { backgroundColor: palette.action.hover },
        }}
      >
        {t('buttons.back')}
      </Button>
      <LoadingButton
        data-testid={OnboardingTestIds.continue}
        onClick={handleNextClick}
        loading={isLoading}
        disabled={disableSubmitButton}
        variant="contained"
        sx={{ width: '50%' }}
      >
        {t('buttons.continue')}
      </LoadingButton>
    </Stack>
  );
};
